import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Building2, Palette, FileText, Sparkles,
  Library, Activity, Shield, LogOut, Coins, Film, CalendarDays,
  FolderOpen, Scissors, Settings, Trophy, Crosshair, Zap, BookOpen, Send, Twitter, Globe, Clapperboard
} from 'lucide-react';

const navConfig = {
  SUPER_ADMIN: [
    { path: '/dashboard', label: 'Kontrol Paneli', icon: LayoutDashboard },
    { path: '/sports/manage', label: 'Spor Yönetimi', icon: Trophy },
    { path: '/sports/matches', label: 'Maç Merkezi', icon: Crosshair },
    { path: '/sports/templates', label: 'İçerik Şablonları', icon: BookOpen },
    { path: '/sports/social', label: 'Sosyal Dağıtım', icon: Send },
    { path: '/social/x', label: 'X Entegrasyonu', icon: Twitter },
    { path: '/sports/automation', label: 'Otomatik Mesajlar', icon: Zap },
    { path: '/tenants', label: 'Bayiler', icon: Building2 },
    { path: '/content/create', label: 'İçerik Üret', icon: Sparkles },
    { path: '/video/create', label: 'Video Oluştur', icon: Film },
    { path: '/video/long', label: 'Uzun Video', icon: Clapperboard },
    { path: '/calendar', label: 'İçerik Takvimi', icon: CalendarDays },
    { path: '/portfolio', label: 'Portfolyo', icon: FolderOpen },
    { path: '/content/library', label: 'Medya Kütüphanesi', icon: Library },
    { path: '/jobs', label: 'İşler', icon: Activity },
    { path: '/admin', label: 'Admin Paneli', icon: Shield },
    { path: '/admin/site', label: 'Site Yönetimi', icon: Globe },
    { path: '/admin/credits', label: 'Kredi Yönetimi', icon: Coins },
  ],
  TENANT_ADMIN: [
    { path: '/dashboard', label: 'Kontrol Paneli', icon: LayoutDashboard },
    { path: '/sports/matches', label: 'Maç Merkezi', icon: Crosshair },
    { path: '/sports/templates', label: 'İçerik Şablonları', icon: BookOpen },
    { path: '/sports/social', label: 'Sosyal Dağıtım', icon: Send },
    { path: '/social/x', label: 'X Entegrasyonu', icon: Twitter },
    { path: '/sports/automation', label: 'Otomatik Mesajlar', icon: Zap },
    { path: '/brand', label: 'Marka Profili', icon: Palette },
    { path: '/content/create', label: 'İçerik Üret', icon: Sparkles },
    { path: '/video/create', label: 'Video Oluştur', icon: Film },
    { path: '/video/long', label: 'Uzun Video', icon: Clapperboard },
    { path: '/calendar', label: 'İçerik Takvimi', icon: CalendarDays },
    { path: '/portfolio', label: 'Portfolyo', icon: FolderOpen },
    { path: '/content/library', label: 'Medya Kütüphanesi', icon: Library },
    { path: '/jobs', label: 'İşler', icon: Activity },
  ],
  EDITOR: [
    { path: '/dashboard', label: 'Kontrol Paneli', icon: LayoutDashboard },
    { path: '/sports/matches', label: 'Maç Merkezi', icon: Crosshair },
    { path: '/content/create', label: 'İçerik Üret', icon: Sparkles },
    { path: '/video/create', label: 'Video Oluştur', icon: Film },
    { path: '/video/long', label: 'Uzun Video', icon: Clapperboard },
    { path: '/portfolio', label: 'Portfolyo', icon: FolderOpen },
    { path: '/content/library', label: 'Medya Kütüphanesi', icon: Library },
  ],
};

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const items = navConfig[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className="w-64 h-screen flex flex-col bg-black/60 backdrop-blur-xl border-r border-white/10 shrink-0"
      data-testid="sidebar"
    >
      <div className="p-6 border-b border-white/10">
        <h1 className="font-outfit text-xl font-bold text-white tracking-tight">
          Sosyal.Team
        </h1>
        <p className="text-xs text-[#737373] mt-1 font-ibm">
          {user?.role?.replace(/_/g, ' ')}
        </p>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map((item) => {
          const active =
            location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path + '/'));
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
              className={`
                flex items-center gap-3 px-6 py-3 text-sm font-ibm
                transition-colors duration-200 border-l-2
                ${active
                  ? 'text-white bg-white/5 border-white'
                  : 'text-[#A3A3A3] hover:text-white hover:bg-white/5 border-transparent'
                }
              `}
            >
              <item.icon size={18} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#FACC15] flex items-center justify-center text-black font-bold text-xs font-outfit">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate font-ibm">{user?.name}</p>
            <p className="text-xs text-[#737373] truncate">{user?.email}</p>
          </div>
        </div>
        <div className="flex gap-1">
          <Link to="/settings" data-testid="nav-settings"
            className="flex items-center gap-2 flex-1 px-3 py-2 text-sm text-[#A3A3A3] hover:text-white hover:bg-white/5 rounded-md transition-colors duration-200">
            <Settings size={16} /> Ayarlar
          </Link>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex items-center gap-2 px-3 py-2 text-sm text-[#A3A3A3] hover:text-white hover:bg-white/5 rounded-md transition-colors duration-200">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
};
